import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  clearDockFaultAPI,
  type Dock,
  type DockListParams,
  getDockStatusesAPI,
  getDocksAPI,
  unlockDockAPI,
  updateDockAPI,
} from "@/apis/dock/dock-apis";
import { queryKeys } from "./keys";

export function useDocks(params: DockListParams = {}, enabled = true) {
  return useQuery({
    queryKey: queryKeys.docks.list(params),
    queryFn: () => getDocksAPI(params),
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useDockStatuses() {
  return useQuery({
    queryKey: queryKeys.enums.dockStatuses,
    queryFn: getDockStatusesAPI,
    staleTime: Infinity,
  });
}

export function useUnlockDock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => unlockDockAPI(id),
    onSuccess: () => {
      // Unlock affects dock state, invalidate all list queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.docks.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useUpdateDock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<Dock> }) =>
      updateDockAPI(id, body),
    onSuccess: () => {
      // Update affects dock state, invalidate all list queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.docks.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useClearDockFault() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => clearDockFaultAPI(id),
    onSuccess: () => {
      // Clearing fault affects dock state, invalidate all dock list queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.docks.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
      // Also invalidate station queries (stations track faultyDockCount)
      queryClient.invalidateQueries({
        queryKey: queryKeys.stations.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}
